import { useEffect, useMemo, useRef, useState } from "react";
import { StyledBox, StyledTextField } from "../../../styled-mui";
import "react-phone-input-2/lib/material.css";
import Autocomplete from "@mui/material/Autocomplete";
import { EMPTY_STRING } from "../../../constants/common";
import { useTranslation } from "react-i18next";
import { SvgChevronDown } from "../../svg/chevronDown";
import { useStorelocatorByLocation } from "../../../utils/hooks/swr/useStorelocatorByLocation";
import { StyledInputStore } from "./styled-inputStore";
import { useDispatch } from "react-redux";
import { STORES_LIST_GET_REQUEST_ACTION } from "../../../redux/actions/stores";
import { searchType } from "../../../types/store-types";
import { useStoreLocatorValue } from "../../../_foundation/context/store-locator-context";
import { Box, Typography } from "@mui/material";

interface StoreInputProps {
  onChange: (e: any, countryCode?: string) => void;
  onBlur: (e: any, countryCode?: string) => void;
  refStore: any;
  error: boolean;
  zipCode?: string;
  required?: boolean;
  dataLoadedOnDefault?: boolean;
  isFromClaim?: boolean;
  isLogin?: boolean;
  setRefStore?: any;
  isFromAppointmentForm?: boolean;
  selectedOption?: string;
  emptyDefaultValue?: boolean;
  cadStores?: any;
  type?: "secondary";
  showDistance?: boolean;
}

const StoreInputContainer: React.FC<StoreInputProps> = ({
  onChange,
  onBlur,
  refStore,
  error = false,
  required = true,
  zipCode = null,
  dataLoadedOnDefault = true,
  isLogin,
  setRefStore,
  isFromClaim,
  isFromAppointmentForm,
  selectedOption,
  cadStores,
  emptyDefaultValue = false,
  type,
  showDistance,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { storeLocator } = useStoreLocatorValue();

  const [storesSelect, setStoresSelect] = useState<any[]>([]);
  const [defaultStoresSelect, setDefaultStoresSelect] = useState<string | null>(null);
  const keepEmptyStoreOnLoad = useRef<boolean>(emptyDefaultValue);

  const fetchedStores = useStorelocatorByLocation(
    dataLoadedOnDefault || (zipCode && zipCode.length >= 5) ? zipCode : "",
    isFromAppointmentForm,
    selectedOption
  );

  const isCadEligible = selectedOption === "CAD" && Array.isArray(cadStores) && cadStores.length > 0;

  useEffect(() => {
    if (fetchedStores?.length > 0) {
      const storesArray = fetchedStores.map((store, index) => {
        let isVisio = false;
        try {
          const attrs = store.Attribute || store.attributes || [];
          if (Array.isArray(attrs)) {
            const vis = attrs.find((a: any) => (a.name || a.displayName || "").toLowerCase() === "isvisio");
            if (vis) {
              isVisio = vis.value === "1";
            }
          }
        } catch (e) {
          // ignore
        }

        return {
          id: store.storeName,
          label: store.name,
          postalCode: store.postalCode?.trim(),
          address: store.address,
          city: store.city,
          country: store.country,
          distance: store.distance != null && !isNaN(parseFloat(store.distance)) ? parseFloat(store.distance) : null,
          key: index,
          isVisio,
          Attribute: store.Attribute,
        };
      });
      let matchedStores: any = [];
      if (selectedOption === "CAD") {
        const homeAvailIds = new Set(cadStores.map((item) => item.idMagasin.replace(/^0+/, "")));

        matchedStores = storesArray.filter((store) => {
          // Try matching with storeName (after removing leading zeros)
          const normalizedId = String(store.id).replace(/^0+/, "");
          return homeAvailIds.has(normalizedId) || homeAvailIds.has(store.id);
        });
      }

      setStoresSelect(matchedStores.length > 0 ? matchedStores : storesArray);

      if (keepEmptyStoreOnLoad.current) {
        keepEmptyStoreOnLoad.current = false;
      } else {
        const storesToUse = matchedStores.length > 0 ? matchedStores : storesArray;

        const selectedStore = storesArray.find(
          (store) => store.postalCode === storeLocator?.selectedStore?.postalCode?.trim()
        );
        if (selectedStore) {
          setDefaultStoresSelect(selectedStore.label);
          setRefStore(selectedStore);
        } else if (storesToUse.length > 0) {
          //set default if no selected store
          setDefaultStoresSelect(storesToUse[0].label);
          setRefStore(storesToUse[0]);

          if (showDistance === true) {
            onChange(storesToUse[0]);
          }
        } else if (storesArray.length > 0) {
          //set default if no selected store
          setDefaultStoresSelect(storesArray[0].label);
          setRefStore(storesArray[0]);
          if (showDistance === true) {
            onChange(storesArray[0]);
          }
        }
      }
    } else {
      if (isFromClaim) {
        getAllStoresList();
      }
      onChange(null); //set ref store in parent component to null
      setStoresSelect([]);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedStores]);

  const handleChange = (newValue: any) => {
    if (newValue) {
      setDefaultStoresSelect(newValue.label);
      setRefStore(newValue);
      onChange(newValue);
    } else {
      setDefaultStoresSelect(null);
      setRefStore(null);
      onChange(null);
    }
  };

  const getAllStoresList = async () => {
    const parameters: any = {
      query: { allStores: true },
    };
    dispatch(
      STORES_LIST_GET_REQUEST_ACTION({
        parameters,
        type: searchType.allStores,
      })
    );
  };

  const storeValue = useMemo(
    () => storesSelect.find((store) => store.label === refStore?.label) || defaultStoresSelect,
    [storesSelect, refStore, defaultStoresSelect]
  );

  return (
    <StyledInputStore>
      <Autocomplete
        disabled={(!dataLoadedOnDefault && storesSelect.length === 0) || isLogin}
        disablePortal
        id="combo-box-stores"
        options={storesSelect}
        getOptionLabel={(option) =>
          showDistance && option.distance != null
            ? `${option.label} - ${option.distance.toFixed(1)} km`
            : option.label || ""
        }
        popupIcon={<SvgChevronDown className="medium" />}
        sx={{ flex: 1, ".MuiAutocomplete-endAdornment": { top: "calc(50% - 20px);" } }}
        value={storeValue}
        onChange={(event, newValue) => {
          if (newValue) {
            const cleanValue = { ...newValue };
            delete cleanValue.distance;
            handleChange(cleanValue);
          } else {
            handleChange(null);
          }
        }}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            variant="standard"
            focused
            className={type === "secondary" ? "styled-v2" : "underline-gray"}
            required={required}
            fullWidth
            onBlur={(e) => onBlur(e)}
            label={
              <>
                {t("Forms.Labels.Store")}{" "}
                {required && (
                  <StyledBox component="span" color="primary.main">
                    *
                  </StyledBox>
                )}
              </>
            }
            error={error && !(!dataLoadedOnDefault && !fetchedStores)}
            helperText={error && !(!dataLoadedOnDefault && !fetchedStores) ? t("ErrorFormMessage.Store") : EMPTY_STRING}
          />
        )}
        renderOption={
          showDistance
            ? (props, option, { selected }) => (
                <li
                  {...props}
                  key={option.id}
                  style={{
                    backgroundColor: selected ? "#ecf8ff" : "inherit",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = selected ? "#ecf8ff" : "inherit")}>
                  <Box display="flex" justifyContent="space-between" width="100%">
                    <Box>
                      {option.label}
                      {option.city && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {option.city}
                        </Typography>
                      )}
                    </Box>
                    {option.distance != null && (
                      <Typography variant="caption" fontSize={12} mt={1} color="text.secondary">
                        {option.distance.toFixed(2)} km
                      </Typography>
                    )}
                  </Box>
                </li>
              )
            : undefined
        }
        ListboxProps={{ className: "autocomplete-listbox" }}
      />
    </StyledInputStore>
  );
};

export { StoreInputContainer };
